import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const API = process.env.REACT_APP_API_URL;

const fetchJSON  = (url) => fetch(url).then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); });
const patchJSON  = (url, body = {}) => fetch(url, { method: "PATCH",  headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }).then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); });
const deleteJSON = (url) => fetch(url, { method: "DELETE" }).then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); });

// ── Thunks ─────────────────────────────────────────────────────────────

export const fetchUserAnalytics    = createAsyncThunk("admin/fetchUsers",      (period = "30d") => fetchJSON(`${API}/api/admin/analytics/users?period=${period}`));
export const fetchRevenue          = createAsyncThunk("admin/fetchRevenue",     () => fetchJSON(`${API}/api/admin/analytics/revenue`));
export const fetchCouponAnalytics  = createAsyncThunk("admin/fetchCoupons",    (category = "") => fetchJSON(`${API}/api/admin/analytics/coupons${category ? `?category=${category}` : ""}`));
export const fetchFraudAlerts      = createAsyncThunk("admin/fetchFraud",       () => fetchJSON(`${API}/api/admin/fraud/alerts`));
export const fetchModerationQueue  = createAsyncThunk("admin/fetchModeration", () => fetchJSON(`${API}/api/admin/moderation/queue`));
export const fetchMLChurn          = createAsyncThunk("admin/fetchMLChurn",    () => fetchJSON(`${API}/api/admin/ml/churn`));
export const fetchMLSentiment      = createAsyncThunk("admin/fetchMLSentiment",() => fetchJSON(`${API}/api/admin/ml/sentiment`));
export const fetchMLForecast       = createAsyncThunk("admin/fetchMLForecast", () => fetchJSON(`${API}/api/admin/ml/forecast`));

export const approveEntity = createAsyncThunk(
    "admin/approve",
    ({ type, id }) => patchJSON(`${API}/api/admin/moderation/approve/${type}/${id}`)
);

export const rejectEntity = createAsyncThunk(
    "admin/reject",
    ({ type, id, reason }) => patchJSON(`${API}/api/admin/moderation/reject/${type}/${id}`, { reason })
);

export const resolveFraudAlert = createAsyncThunk(
    "admin/resolveFraud",
    (id) => patchJSON(`${API}/api/admin/fraud/alerts/${id}/resolve`)
);

export const approveReview = createAsyncThunk(
    "admin/approveReview",
    (id) => patchJSON(`${API}/api/admin/moderation/approve-review/${id}`)
);

export const deleteUser     = createAsyncThunk("admin/deleteUser",     (id) => deleteJSON(`${API}/api/admin/delete/user/${id}`));
export const deleteVendor   = createAsyncThunk("admin/deleteVendor",   (id) => deleteJSON(`${API}/api/admin/delete/vendor/${id}`));
export const deleteBusiness = createAsyncThunk("admin/deleteBusiness", (id) => deleteJSON(`${API}/api/admin/delete/business/${id}`));
export const deleteCoupon   = createAsyncThunk("admin/deleteCoupon",   (id) => deleteJSON(`${API}/api/admin/delete/coupon/${id}`));

// ── Slice ──────────────────────────────────────────────────────────────

const initialSectionState = { data: null, loading: false, error: null };

const adminSlice = createSlice({
    name: "admin",
    initialState: {
        users:      { ...initialSectionState },
        revenue:    { ...initialSectionState },
        coupons:    { ...initialSectionState },
        fraud:      { ...initialSectionState },
        moderation: { ...initialSectionState },
        ml: {
            churn:     { ...initialSectionState },
            sentiment: { ...initialSectionState },
            forecast:  { ...initialSectionState },
        },
        period: "30d",
    },
    reducers: {
        setPeriod(state, action) {
            state.period = action.payload;
        },
        clearSection(state, action) {
            const key = action.payload;
            if (state[key]) state[key] = { ...initialSectionState };
        },
    },
    extraReducers: (builder) => {
        const handle = (thunk, path) => {
            const set = (state, key, patch) => {
                const parts = path.split(".");
                let obj = state;
                for (let i = 0; i < parts.length - 1; i++) obj = obj[parts[i]];
                Object.assign(obj[parts[parts.length - 1]], patch);
            };
            builder
                .addCase(thunk.pending,   (s) => set(s, path, { loading: true,  error: null }))
                .addCase(thunk.fulfilled, (s, a) => set(s, path, { loading: false, data: a.payload }))
                .addCase(thunk.rejected,  (s, a) => set(s, path, { loading: false, error: a.error.message }));
        };

        handle(fetchUserAnalytics,   "users");
        handle(fetchRevenue,         "revenue");
        handle(fetchCouponAnalytics, "coupons");
        handle(fetchFraudAlerts,     "fraud");
        handle(fetchModerationQueue, "moderation");
        handle(fetchMLChurn,         "ml.churn");
        handle(fetchMLSentiment,     "ml.sentiment");
        handle(fetchMLForecast,      "ml.forecast");

        builder.addCase(approveEntity.fulfilled,    () => {});
        builder.addCase(rejectEntity.fulfilled,     () => {});
        builder.addCase(resolveFraudAlert.fulfilled, () => {});
        builder.addCase(approveReview.fulfilled,    () => {});
        builder.addCase(deleteUser.fulfilled,     () => {});
        builder.addCase(deleteVendor.fulfilled,   () => {});
        builder.addCase(deleteBusiness.fulfilled, () => {});
        builder.addCase(deleteCoupon.fulfilled,   () => {});
    },
});

export const { setPeriod, clearSection } = adminSlice.actions;
export default adminSlice.reducer;
